/**
 * Return portal on the returns page (sections/bjaf-help-returns.liquid, setting "Return portal").
 * Three steps: find the order, pick items and a reason and submit, then what to do with each item. The page never
 * says beforehand which items come back: the app decides on submit (labels for what comes back, photo proof for what
 * is thrown away, photos + "a new one" or "money back" for damaged or wrong items). Talks to the Bjaf Returns app through the app proxy (data-endpoint, e.g. /apps/returns or
 * /de/apps/returns): POST lookup, create, photo. The app answers with codes, amounts in cents and product titles;
 * every text comes from the page ([data-bjaf-rp-strings] and the markup). Photos are shrunk in the browser
 * (longest side 1600px, JPEG) and sent one per request.
 * Return methods: Bjaf manages them in admin (metaobject bjaf_return_method); the names come from [data-bjaf-rp-methods],
 * the app says which fit the order's country. With more than one the customer picks, then POST book makes the label.
 * Status (2026-11-03): the lookup also lists the order's earlier returns (status codes from the app); a customer who
 * comes back sees them first (step "status"), with links to the label and tracking, then can return something else.
 * Events: Shopify custom events bjaf_return_lookup, bjaf_return_created, bjaf_return_method, bjaf_return_photos,
 * bjaf_return_status.
 */
(function () {
  if (window.bjafReturnsReady) return;
  window.bjafReturnsReady = true;

  var STEPS = ['find', 'items', 'done'];
  var MAX_PHOTOS = 3;

  function track(name, data) {
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, data || {});
      (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, data || {}));
    } catch (e) {}
  }

  function fill(text, map) {
    return String(text || '').replace(/\[(\w+)\]/g, function (m, k) {
      return map[k] != null ? map[k] : m;
    });
  }

  // Longest side 1600px, JPEG 0.8: a phone photo ends up around 200-500 KB.
  function shrink(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        var scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
        var canvas = document.createElement('canvas');
        canvas.width = Math.round(img.naturalWidth * scale);
        canvas.height = Math.round(img.naturalHeight * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error('unreadable'));
      };
      img.src = url;
    });
  }

  function init(root) {
    // Liquid's t filter HTML-escapes ("couldn&#39;t"); decode once, since the texts go in with textContent.
    var S = JSON.parse(root.querySelector('[data-bjaf-rp-strings]').textContent);
    var decoder = document.createElement('textarea');
    Object.keys(S).forEach(function (k) {
      decoder.innerHTML = S[k];
      S[k] = decoder.value;
    });
    var METHODS = {};
    try {
      METHODS = JSON.parse(root.querySelector('[data-bjaf-rp-methods]').textContent) || {};
    } catch (e) {}
    var endpoint = root.getAttribute('data-endpoint');
    var lang = document.documentElement.lang || 'en';
    // "Withdraw from contract" (EU withdrawal function): same steps, no reason asked, a "Confirm withdrawal" button.
    var withdraw = /(?:^|[?&])withdraw=1(?:&|$)/.test(location.search.slice(1));
    var state;

    function reset() {
      state = { token: null, order: null, picked: {}, qty: {}, reason: null, busy: false };
    }
    reset();

    var step = function (name) { return root.querySelector('[data-step="' + name + '"]'); };
    var errorEl = function (name) { return step(name).querySelector('[data-bjaf-rp-error]'); };

    function money(cents) {
      try {
        return new Intl.NumberFormat(lang, { style: 'currency', currency: state.order.currency }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2) + ' ' + state.order.currency;
      }
    }

    function show(name) {
      // The status view sits outside the three steps: no progress bar there.
      step('status').hidden = name !== 'status';
      root.querySelector('.bjaf-rp__top').hidden = name === 'status';
      if (name === 'status') {
        STEPS.forEach(function (s) { step(s).hidden = true; });
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        step('status').querySelector('h2').focus({ preventScroll: true });
        return;
      }
      STEPS.forEach(function (s) { step(s).hidden = s !== name; });
      var idx = STEPS.indexOf(name);
      root.querySelector('[data-bjaf-rp-step-label]').textContent = fill(S.step, { n: idx + 1, total: STEPS.length });
      root.querySelectorAll('.bjaf-rp__progress span').forEach(function (bar, i) { bar.classList.toggle('is-on', i <= idx); });
      if (idx > 0) {
        var heading = Array.prototype.find.call(step(name).querySelectorAll('h2'), function (h) { return h.offsetParent !== null; });
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (heading) heading.focus({ preventScroll: true });
      }
    }

    function setError(el, text) {
      el.textContent = text || '';
      el.hidden = !text;
    }

    function busy(button, on) {
      state.busy = on;
      button.disabled = on;
      button.classList.toggle('is-busy', on);
    }

    function api(route, body) {
      return fetch(endpoint + '/' + route, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (res) {
          return res.json().catch(function () { return { ok: false, error: 'server_error' }; });
        })
        .catch(function () { return { ok: false, error: 'network' }; });
    }

    function errorText(code) {
      return {
        not_found: S.notFound,
        nothing_returnable: S.nothingReturnable,
        session_expired: S.sessionExpired,
        phone_required: S.phoneRequired,
        bad_method: S.methodMissing
      }[code] || S.error;
    }

    // Step 1
    var form = step('find');
    if (withdraw) {
      form.querySelector('h2').textContent = S.withdrawHeading;
      var lead = form.querySelector('.bjaf-rp__lead');
      if (!lead) {
        lead = document.createElement('p');
        lead.className = 'bjaf-rp__lead';
        form.querySelector('h2').after(lead);
      }
      lead.textContent = S.withdrawIntro;
      form.querySelector('[data-bjaf-rp-withdraw-link]').hidden = true;
      step('items').querySelector('.bjaf-rp__reasons').hidden = true;
      step('items').querySelector('[data-bjaf-rp-submit]').textContent = S.withdrawConfirm;
    }
    // From the customer account ("Start a return" in the order menu): the order number comes with the link, so only
    // the email is left to type.
    var prefill = (/(?:^|[?&])order=([^&#]*)/.exec(location.search.slice(1)) || [])[1];
    if (prefill && !form.elements.order.value) {
      try {
        form.elements.order.value = decodeURIComponent(prefill).slice(0, 30);
      } catch (e) {}
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (state.busy) return;
      var orderNumber = form.elements.order.value.trim();
      var email = form.elements.email.value.trim();
      if (!orderNumber || !email) return setError(errorEl('find'), S.missing);
      setError(errorEl('find'), '');
      var button = form.querySelector('button[type="submit"]');
      busy(button, true);
      api('lookup', { orderNumber: orderNumber, email: email, withStatus: true }).then(function (res) {
        busy(button, false);
        track('bjaf_return_lookup', { ok: !!res.ok, error: res.error || null });
        if (!res.ok) return setError(errorEl('find'), errorText(res.error));
        state.token = res.token;
        state.order = res.order;
        var returns = res.order.returns || [];
        renderStatus(returns);
        if ((res.order.pendingProof && res.order.pendingProof.length) || (res.order.pendingLabels && res.order.pendingLabels.length)) {
          state.pendingView = true;
          renderPending(res.order.pendingProof, res.order.pendingLabels);
          return show('done');
        }
        // Coming back: the status of earlier returns first.
        if (returns.length && !withdraw) {
          track('bjaf_return_status', { count: returns.length });
          return show('status');
        }
        renderItems();
        show('items');
      });
    });

    // Step 2
    var itemsStep = step('items');
    var list = itemsStep.querySelector('[data-bjaf-rp-items]');
    var tpl = itemsStep.querySelector('[data-bjaf-rp-item-template]');
    var noteField = itemsStep.querySelector('[data-bjaf-rp-note]');
    var phoneField = itemsStep.querySelector('[data-bjaf-rp-phone]');

    function syncFields() {
      noteField.hidden = !(state.reason === 'damaged' || state.reason === 'wrong');
      var anyPicked = Object.keys(state.picked).some(function (id) { return state.picked[id]; });
      phoneField.hidden = !(state.order.needsPhone && anyPicked);
    }

    function renderItems() {
      itemsStep.querySelector('[data-bjaf-rp-status-link]').hidden = !(state.order.returns && state.order.returns.length);
      itemsStep.querySelector('[data-bjaf-rp-order-meta]').textContent = withdraw && state.order.customer
        ? fill(S.withdrawFor, { name: state.order.customer, order: state.order.name })
        : fill(S.orderMeta, { order: state.order.name });
      var notice = itemsStep.querySelector('[data-bjaf-rp-window]');
      notice.textContent = S.outsideWindow;
      notice.hidden = state.order.insideWindow;
      list.textContent = '';
      state.picked = {};
      state.qty = {};
      state.reason = withdraw ? 'mind' : null;
      state.order.items.forEach(function (item) {
        var row = tpl.content.firstElementChild.cloneNode(true);
        var button = row.querySelector('.bjaf-rp__item');
        var img = row.querySelector('img');
        if (item.image) img.src = item.image;
        else img.remove();
        row.querySelector('[data-title]').textContent = item.title;
        var variant = row.querySelector('[data-variant]');
        variant.textContent = item.variantTitle || '';
        variant.hidden = !item.variantTitle;
        row.querySelector('[data-price]').textContent = money(item.unitCents);
        var qtyLabel = row.querySelector('.bjaf-rp__qty');
        var select = qtyLabel.querySelector('select');
        for (var n = 1; n <= item.returnableQty; n++) select.add(new Option(String(n), String(n)));
        state.qty[item.id] = 1;
        select.addEventListener('change', function () { state.qty[item.id] = Number(select.value); });
        button.addEventListener('click', function () {
          state.picked[item.id] = !state.picked[item.id];
          button.setAttribute('aria-pressed', String(state.picked[item.id]));
          button.classList.toggle('is-picked', state.picked[item.id]);
          qtyLabel.hidden = !(state.picked[item.id] && item.returnableQty > 1);
          syncFields();
        });
        list.appendChild(row);
      });
      itemsStep.querySelectorAll('[data-reason]').forEach(function (chip) {
        chip.setAttribute('aria-pressed', 'false');
        chip.classList.remove('is-picked');
      });
      syncFields();
    }

    itemsStep.querySelectorAll('[data-reason]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        state.reason = chip.getAttribute('data-reason');
        itemsStep.querySelectorAll('[data-reason]').forEach(function (c) {
          var on = c === chip;
          c.setAttribute('aria-pressed', String(on));
          c.classList.toggle('is-picked', on);
        });
        syncFields();
      });
    });

    function selection() {
      return Object.keys(state.picked)
        .filter(function (id) { return state.picked[id]; })
        .map(function (id) { return { id: id, quantity: state.qty[id] || 1 }; });
    }

    itemsStep.querySelector('[data-bjaf-rp-submit]').addEventListener('click', function () {
      if (state.busy) return;
      var items = selection();
      if (!items.length || !state.reason) return setError(errorEl('items'), S.missingItems);
      if (!phoneField.hidden && !phoneField.querySelector('input').value.trim()) return setError(errorEl('items'), S.phoneRequired);
      setError(errorEl('items'), '');
      var button = this;
      busy(button, true);
      api('create', {
        token: state.token,
        items: items,
        reason: state.reason,
        withdraw: withdraw,
        phone: phoneField.querySelector('input').value.trim(),
        note: noteField.querySelector('textarea').value.trim()
      }).then(function (res) {
        busy(button, false);
        if (res.error === 'session_expired') {
          reset();
          show('find');
          return setError(errorEl('find'), S.sessionExpired);
        }
        if (!res.ok) return setError(errorEl('items'), errorText(res.error));
        track('bjaf_return_created', {
          reason: state.reason,
          withdraw: withdraw,
          send: res.send ? res.send.status : null,
          dispose: res.dispose ? res.dispose.status : null
        });
        renderDone(res);
        show('done');
      });
    });

    // Step 3
    var doneStep = step('done');
    var disposes = doneStep.querySelector('[data-bjaf-rp-disposes]');
    var proofTpl = doneStep.querySelector('[data-bjaf-rp-proof-template]');

    function fillTitles(container, titles) {
      var ul = container.querySelector('[data-lines]');
      ul.textContent = '';
      titles.forEach(function (t) {
        var li = document.createElement('li');
        li.textContent = t;
        ul.appendChild(li);
      });
    }

    function heading(which) {
      doneStep.querySelector('[data-done="registered"]').hidden = which !== 'registered';
      doneStep.querySelector('[data-done="pending"]').hidden = which !== 'pending';
    }

    // kind 'dispose' (thrown away) or 'claim' (damaged or wrong: photos + a new one or money back).
    function addProofCard(kind, titles, proofToken) {
      var card = proofTpl.content.firstElementChild.cloneNode(true);
      card.querySelectorAll('[data-only]').forEach(function (el) {
        if (el.getAttribute('data-only') !== kind) el.remove();
      });
      fillTitles(card, titles);
      var input = card.querySelector('input[type="file"]');
      var previews = card.querySelector('[data-previews]');
      var error = card.querySelector('[data-photo-error]');
      var sendButton = card.querySelector('[data-send-photos]');
      var files = [];
      var choice = null;

      card.querySelectorAll('[data-choice]').forEach(function (chip) {
        chip.addEventListener('click', function () {
          choice = chip.getAttribute('data-choice');
          card.querySelectorAll('[data-choice]').forEach(function (c) {
            var on = c === chip;
            c.setAttribute('aria-pressed', String(on));
            c.classList.toggle('is-picked', on);
          });
          setError(error, '');
        });
      });

      input.addEventListener('change', function () {
        files = Array.prototype.slice.call(input.files || [], 0, MAX_PHOTOS);
        previews.textContent = '';
        files.forEach(function (f) {
          var li = document.createElement('li');
          var img = document.createElement('img');
          img.alt = '';
          img.src = URL.createObjectURL(f);
          li.appendChild(img);
          previews.appendChild(li);
        });
        setError(error, '');
      });

      sendButton.addEventListener('click', function () {
        if (state.busy) return;
        if (kind === 'claim' && !choice) return setError(error, S.claimMissing);
        if (!files.length) return setError(error, S.photoMissing);
        setError(error, '');
        busy(sendButton, true);
        var sent = 0;
        files
          .reduce(function (chain, file) {
            return chain.then(function () {
              return shrink(file).then(function (dataUrl) {
                return api('photo', { proofToken: proofToken, image: dataUrl, choice: choice }).then(function (res) {
                  if (!res.ok) throw new Error(res.error || 'upload');
                  sent += 1;
                });
              });
            });
          }, Promise.resolve())
          .then(function () {
            busy(sendButton, false);
            track('bjaf_return_photos', { count: sent, kind: kind, choice: choice });
            card.querySelector('[data-upload]').hidden = true;
            card.querySelector('[data-photo-sent]').hidden = false;
          })
          .catch(function () {
            busy(sendButton, false);
            setError(error, S.photoError);
          });
      });
      disposes.appendChild(card);
    }

    // The send card: a choice of methods first when there is more than one, then the label.
    function renderSend(s, sendToken) {
      var sendCard = doneStep.querySelector('[data-bjaf-rp-send]');
      var manualCard = doneStep.querySelector('[data-bjaf-rp-sendmanual]');
      sendCard.hidden = !s || s.status === 'requested';
      manualCard.hidden = !s || s.status !== 'requested';
      if (!s) return;
      if (s.status === 'requested') return fillTitles(manualCard, s.titles);
      fillTitles(sendCard, s.titles);
      sendCard.querySelector('[data-send-text]').textContent =
        s.refundSendAt === 'confirm' ? S.sendConfirm : s.refundSendAt === 'received' ? S.sendArrival : S.sendScan;
      var fee = sendCard.querySelector('[data-send-fee]');
      fee.hidden = !(s.feeCents > 0);
      if (!fee.hidden) fee.textContent = fill(S.sendFee, { amount: money(s.feeCents) });

      var methodBox = sendCard.querySelector('[data-method-box]');
      var box = sendCard.querySelector('[data-label-box]');
      methodBox.hidden = s.status !== 'choose_method';
      box.hidden = !s.labelUrl;
      sendCard.querySelector('[data-label-failed]').hidden = s.status !== 'label_failed';

      if (s.status === 'choose_method') {
        var list = methodBox.querySelector('[data-method-list]');
        var err = methodBox.querySelector('[data-method-error]');
        var picked = null;
        list.textContent = '';
        s.methods.forEach(function (handle) {
          var m = METHODS[handle] || { title: handle, text: '' };
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'bjaf-rp__item bjaf-rp__method';
          b.setAttribute('aria-pressed', 'false');
          var t = document.createElement('span');
          t.className = 'bjaf-rp__item-text';
          var strong = document.createElement('strong');
          strong.textContent = m.title;
          t.appendChild(strong);
          if (m.text) {
            var small = document.createElement('span');
            small.textContent = m.text;
            t.appendChild(small);
          }
          b.appendChild(t);
          b.addEventListener('click', function () {
            picked = handle;
            list.querySelectorAll('.bjaf-rp__method').forEach(function (x) {
              var on = x === b;
              x.classList.toggle('is-picked', on);
              x.setAttribute('aria-pressed', String(on));
            });
            setError(err, '');
          });
          list.appendChild(b);
        });
        var bookButton = methodBox.querySelector('[data-book]');
        bookButton.onclick = function () {
          if (state.busy) return;
          if (!picked) return setError(err, S.methodMissing);
          busy(bookButton, true);
          api('book', { sendToken: s.sendToken || sendToken, method: picked }).then(function (res) {
            busy(bookButton, false);
            if (!res.ok) return setError(err, errorText(res.error));
            track('bjaf_return_method', { method: picked });
            renderSend(res.send);
          });
        };
        return;
      }
      if (s.labelUrl) {
        box.querySelector('[data-label-link]').href = s.labelUrl;
        var code = box.querySelector('[data-code]');
        code.textContent = fill(S.code, { code: s.labellessCode || '' });
        code.hidden = !s.labellessCode;
        var trackLink = box.querySelector('[data-track-link]');
        trackLink.hidden = !s.trackingUrl;
        if (s.trackingUrl) trackLink.href = s.trackingUrl;
      }
    }

    function renderDone(res) {
      heading('registered');
      var receipt = doneStep.querySelector('[data-bjaf-rp-receipt]');
      receipt.hidden = !res.receivedAt;
      if (res.receivedAt) {
        var when = new Date(res.receivedAt);
        try {
          when = new Intl.DateTimeFormat(lang, { dateStyle: 'long', timeStyle: 'short' }).format(when);
        } catch (e) {
          when = when.toLocaleString();
        }
        receipt.textContent = fill(S.withdrawReceived, { date: when });
      }
      renderSend(res.send);
      disposes.textContent = '';
      if (res.dispose) addProofCard('dispose', res.dispose.titles, res.dispose.proofToken);
      if (res.claim) addProofCard('claim', res.claim.titles, res.claim.proofToken);
    }

    // Coming back later: photos still missing and/or a send method still to choose.
    function renderPending(pending, labels) {
      heading('pending');
      var l = (labels || [])[0];
      renderSend(l ? { status: 'choose_method', titles: l.titles, methods: l.methods, sendToken: l.sendToken, refundSendAt: 'first_scan', feeCents: 0 } : null);
      disposes.textContent = '';
      (pending || []).forEach(function (p) { addProofCard(p.kind || 'dispose', p.titles, p.proofToken); });
    }

    // Status of earlier returns (codes from the app, texts from the page).
    var statusStep = step('status');
    var statusList = statusStep.querySelector('[data-bjaf-rp-status-list]');
    var statusTpl = statusStep.querySelector('[data-bjaf-rp-status-template]');
    var STATUS = {
      choose_method: ['stChoose', 'stChooseText'],
      label_ready: ['stLabel', 'stLabelText'],
      label_coming: ['stLabelComing', 'labelFailed'],
      photo_needed: ['stPhoto', 'stPhotoText'],
      in_review: ['stReview', 'stReviewText'],
      arranging: ['stArranging', 'stArrangingText'],
      refunded: ['stRefunded', 'stRefundedText'],
      replaced: ['stReplaced', 'stReplacedText'],
      declined: ['stDeclined', 'stDeclinedText'],
      cancelled: ['stCancelled', 'stCancelledText']
    };

    function day(iso) {
      if (!iso) return '';
      try {
        return new Intl.DateTimeFormat(lang, { dateStyle: 'long' }).format(new Date(iso));
      } catch (e) {
        return iso.slice(0, 10);
      }
    }

    function renderStatus(returns) {
      statusStep.querySelector('[data-bjaf-rp-status-meta]').textContent = fill(S.orderMeta, { order: state.order.name });
      statusStep.querySelector('[data-bjaf-rp-status-more]').hidden = !state.order.items.length || withdraw;
      statusList.textContent = '';
      returns.forEach(function (r) {
        var keys = STATUS[r.status] || STATUS.in_review;
        var card = statusTpl.content.firstElementChild.cloneNode(true);
        card.querySelector('[data-status-badge]').textContent = S[keys[0]];
        card.querySelector('[data-status-started]').textContent = fill(S.statusStarted, { date: day(r.at) });
        fillTitles(card, r.titles || []);
        var text = S[keys[1]];
        if (r.status === 'refunded') text = r.refundCents > 0 ? fill(S.stRefundedText, { amount: money(r.refundCents), date: day(r.refundedAt) }) : fill(S.stRefundedPlain, { date: day(r.refundedAt) });
        if (r.status === 'replaced') text = fill(S.stReplacedText, { order: r.replacementOrder || '' });
        card.querySelector('[data-status-text]').textContent = text;
        var label = card.querySelector('[data-status-label]');
        label.hidden = !r.labelUrl;
        if (r.labelUrl) label.href = r.labelUrl;
        var code = card.querySelector('[data-status-code]');
        code.hidden = !r.labellessCode;
        if (r.labellessCode) code.textContent = fill(S.code, { code: r.labellessCode });
        var trackLink = card.querySelector('[data-status-track]');
        trackLink.hidden = !r.trackingUrl;
        if (r.trackingUrl) trackLink.href = r.trackingUrl;
        statusList.appendChild(card);
      });
    }

    statusStep.querySelector('[data-bjaf-rp-status-more]').addEventListener('click', function () {
      renderItems();
      show('items');
    });
    itemsStep.querySelector('[data-bjaf-rp-status-link]').addEventListener('click', function () { show('status'); });
    statusStep.querySelector('[data-bjaf-rp-status-restart]').addEventListener('click', function () {
      reset();
      form.reset();
      show('find');
      form.querySelector('h2').focus();
    });

    doneStep.querySelector('[data-bjaf-rp-restart]').addEventListener('click', function () {
      // From the "still waiting for your photos" view, go straight on to the order's other items.
      if (state.pendingView && state.order && state.order.items.length) {
        state.pendingView = false;
        renderItems();
        return show('items');
      }
      reset();
      form.reset();
      itemsStep.querySelector('textarea').value = '';
      phoneField.querySelector('input').value = '';
      show('find');
      form.querySelector('h2').focus();
    });

    show('find');
  }

  function start() {
    document.querySelectorAll('[data-bjaf-rp]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
